import { SourcingChannelDetails } from 'api-clients/monolith/models/SourcingChannelDetails';
import clsx from 'clsx';
import { produce } from 'immer';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { INDEED } from '@/types/channels';

import { useChannelDescription } from './use-channel-description';

interface ChannelCardProps {
  channel: SourcingChannelDetails;
  indeedIntegrationEnabled?: boolean;
}

export function ChannelCard({
  channel,
  indeedIntegrationEnabled,
}: ChannelCardProps) {
  const intl = useIntl();
  const { getDescription } = useChannelDescription();
  const {
    selectedChannelSlugs,
    setSelectedChannelSlugs,
    selectedOpeningIds,
    setUnsupportedOpenings,
  } = useJobPostingContext();

  const isSelected = selectedChannelSlugs.includes(channel.slug);
  const isIndeedIntegrationDisabled =
    channel.slug === INDEED && !indeedIntegrationEnabled;
  const unsupportedOpeningsCount = channel.unsupported_openings?.length ?? 0;
  const hasUnsupportedOpenings = unsupportedOpeningsCount > 0;
  const allOpeningsUnsupported =
    unsupportedOpeningsCount === selectedOpeningIds.length;
  const isManagedByAssist = channel.managed_by_assist;
  const notPublishable =
    !channel.is_publishable ||
    allOpeningsUnsupported ||
    isIndeedIntegrationDisabled;
  const handleSelectChannelToggle = () => {
    if (allOpeningsUnsupported) return;
    const channelSlug = channel.slug;

    setSelectedChannelSlugs((prev: string[]) => {
      if (prev.includes(channelSlug)) {
        return prev.filter(slug => slug !== channelSlug);
      }
      return [...prev, channelSlug];
    });

    if (hasUnsupportedOpenings) {
      setUnsupportedOpenings((prev: Record<string, string[]>) => {
        return produce(prev, draft => {
          if (isSelected) {
            delete draft[channelSlug];
          } else {
            draft[channelSlug] = channel.unsupported_openings ?? [];
          }
        });
      });
    }
  };

  const description = useMemo(() => {
    if (isIndeedIntegrationDisabled)
      return (
        <span className="italic">
          {intl.formatMessage({
            defaultMessage:
              'Indeed integration is not enabled for this account.',
          })}
        </span>
      );
    if (isManagedByAssist && notPublishable)
      return (
        <span className="italic">
          {intl.formatMessage({
            defaultMessage: 'This channel is managed by Fountain Assist.',
          })}
        </span>
      );

    if (hasUnsupportedOpenings)
      return (
        <span className="italic">
          {intl.formatMessage(
            {
              defaultMessage: `{total, plural,
                one {The selected opening cannot be sponsored by {channel} due to the location.}
                other {{count} of {total} selected openings cannot be sponsored by {channel} due to the location.}
              }`,
            },
            {
              count: unsupportedOpeningsCount,
              total: selectedOpeningIds.length,
              channel: channel.name,
            },
          )}
        </span>
      );

    return getDescription(channel.slug);
  }, [
    channel.name,
    channel.slug,
    getDescription,
    hasUnsupportedOpenings,
    intl,
    isIndeedIntegrationDisabled,
    selectedOpeningIds.length,
    unsupportedOpeningsCount,
    isManagedByAssist,
    notPublishable,
  ]);

  return (
    <div
      className={clsx(
        'border-fx-neutral-200 w-90 flex items-center gap-4 rounded-lg border p-4',
        notPublishable && 'bg-fx-neutral-50',
      )}
    >
      <Checkbox
        checked={isSelected}
        disabled={notPublishable}
        onCheckedChange={handleSelectChannelToggle}
      />
      <div className="flex flex-col items-start justify-center gap-4">
        <ChannelIcon channelSlug={channel.slug} variant="large" />
        <Typography variant="body2">
          <FormattedMessage
            defaultMessage="Post to {channelName}"
            values={{ channelName: channel.name }}
            description="Message to post to a channel, {channelName} is the name of the channel"
          />
        </Typography>
        <Typography variant="subtitle1" className="-mt-3">
          {description}
        </Typography>
      </div>
    </div>
  );
}
